// Shared card shell and states for the home dashboard tiles.

import type { ReactNode } from 'react';
import { FilterLabel } from '@/components/FilterLabel';
import { MUTED_TEXT } from './tileText';

type Props = {
  label: string;
  title: string;
  caption?: string;
  /** Optional element on the right of the header (e.g. a toggle). */
  action?: ReactNode;
  className?: string;
  children: ReactNode;
};

export function TileShell({ label, title, caption, action, className, children }: Props) {
  return (
    <article
      className={`rounded-lg bg-amp-white shadow-1 px-[18px] py-4 flex flex-col gap-3 dark:bg-amp-dark-surface${
        className ? ` ${className}` : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <FilterLabel as="div">{label}</FilterLabel>
          <h2 className="text-[17px] font-semibold text-amp-ink dark:text-amp-dark-ink">{title}</h2>
          {caption && <p className={`mt-0.5 text-[12px] ${MUTED_TEXT}`}>{caption}</p>}
        </div>
        {action}
      </div>
      {children}
    </article>
  );
}

export function TileSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="h-4 rounded bg-amp-cream dark:bg-amp-dark-surface-warm animate-pulse"
          style={{ width: `${95 - i * 12}%` }}
        />
      ))}
    </div>
  );
}

export function TileMessage({ children }: { children: ReactNode }) {
  return <p className={`py-4 text-[13px] ${MUTED_TEXT}`}>{children}</p>;
}
